/**
 * The organisation's brain: what everyone may know (Knowledge first: decisions, conventions, summaries), filed here or promoted and mirrored from
 * projects, with the index and a search across it (Index, the old Ask); and, for administrators, who is cleared to read what
 * (Access): the levels board, compartments, clearances, Check access and "Stop someone reading this".
 *
 * The data column (Layout.tsx): the header, why memory can look empty (an Explainer, when it can
 * apply), the numbers, the Ask and Access views, then list groups.
 */

import { Database, Layers, Lock, Persons } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { useSearchParams } from 'react-router-dom';

import { routes } from '../api/client.ts';
import { MemoryAccessNote } from '../components/memory.tsx';
import { AccessView } from '../components/memoryAccess.tsx';
import { useAccessModel } from '../components/memoryAccessModel.ts';
import { ProjectMemoryPanel } from '../components/projectMemory.tsx';
import { KnowledgePanel } from '../components/memoryKnowledge.tsx';
import { Button, ErrorNote, PageColumn, PageHeader, Stat } from '../components/ui.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { useLoad } from '../hooks.ts';
import { useMay } from '../permissions.ts';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';

type View = 'knowledge' | 'ask' | 'access';

export function MemoryPage() {
  const tone = VIEW_TONES.memory!;
  const [params, setParams] = useSearchParams();
  const status = useLoad(() => routes.orgMemoryStatus(), [], 30_000);
  const projects = useLoad(() => routes.projects().catch(() => []), []);
  // Manage on memory (today: administrators): its readers, compartments and clearances.
  const admin = { data: useMay('data.memory', 'manage') };
  const access = useAccessModel(admin.data === true);
  const asked = params.get('view');
  const view: View = admin.data && asked === 'access' ? 'access' : asked === 'ask' ? 'ask' : 'knowledge';
  const setView = (v: View) => setParams(v === 'knowledge' ? {} : { view: v }, { replace: true });
  // Writing the organisation's memory (memory:write): the same rule runs its tidy.
  const writer = useMay('data.memory', 'update');
  const names = new Map((projects.data ?? []).map((p) => [p.id, p.name]));
  const s = status.data;
  const m = access.data;
  const raised = m?.clearances.filter((c) => c.level === 'confidential' || c.level === 'restricted').length;
  return (
    <PageColumn width="data">
      <PageHeader
        tone={tone}
        icon={<Database />}
        title="Memory"
        subtitle="What the organisation knows, and who is cleared to read it."
        actions={
          <Button size="sm" variant="secondary" isPending={status.loading && Boolean(s)} onPress={() => (status.reload(), access.reload())}>
            Refresh
          </Button>
        }
      />
      {admin.data !== undefined && <MemoryAccessNote admin={admin.data} tone={tone} />}
      <ErrorNote error={status.error} />
      <div className="art-stats">
        <Stat tone={tone} icon={<Database />} label="Items" value={s?.items} detail={s ? `${Object.keys(s.byKind).length} kinds, ${s.mirrored.length} mirrored projects` : ''} />
        {m ? (
          <>
            <Stat tone={tone} icon={<Layers />} label="Above Internal" value={raised} detail={raised ? 'People and groups raised' : 'Everyone reads up to Internal'} onPress={() => setView('access')} />
            <Stat tone={tone} icon={<Lock />} label="Compartments" value={m.compartments.length} detail={`${m.compartments.reduce((n, c) => n + c.grants.length, 0)} holders in all`} onPress={() => setView('access')} />
          </>
        ) : (
          <Stat tone={tone} icon={<Persons />} label="Sources" value={s?.roots.length} detail="Filed directly, or from a project" />
        )}
      </div>
      <Segment aria-label="Memory view" size="sm" selectedKey={view} onSelectionChange={(k) => setView(String(k) as View)} className="w-fit">
        {(admin.data ? (['knowledge', 'ask', 'access'] as const) : (['knowledge', 'ask'] as const)).map((v) => (
          <Segment.Item key={v} id={v}>
            {v === 'knowledge' ? 'Knowledge' : v === 'ask' ? 'Index' : 'Access'}
          </Segment.Item>
        ))}
      </Segment>
      <PartBoundary key="knowledge" name="Knowledge" tone={tone}>
        {view === 'knowledge' && <KnowledgePanel canWrite={writer === true} />}
      </PartBoundary>
      <PartBoundary key="ask" name="Project memory" tone={tone}>
        {view === 'ask' && <ProjectMemoryPanel projectNames={names} />}
      </PartBoundary>
      <PartBoundary key="access" name="Memory access" tone={tone}>
        {view === 'access' && (
          <>
            <ErrorNote error={access.error} />
            {m && <AccessView model={m} reload={access.reload} />}
          </>
        )}
      </PartBoundary>
    </PageColumn>
  );
}
